import React from "react";
import { Link, graphql, useStaticQuery } from "gatsby";
import { GatsbyImage } from "gatsby-plugin-image";
import { FiCheck } from "@react-icons/all-files/fi/FiCheck";
import { FiDownload } from "@react-icons/all-files/fi/FiDownload";
import { FiCalendar } from "@react-icons/all-files/fi/FiCalendar";
import { FiMail } from "@react-icons/all-files/fi/FiMail";
import { PATH } from "~/constant/path";
import { colors } from "~/utils/colors";
import { breakPoints } from "~/utils/variables";

/*
 * ページ途中に置く簡易版CTA。
 * フッター直前の AskUs と同じ「全面オレンジ＋大きなカード」だとページの終わりに見えてしまうため、
 * 白背景の中に収まる低い帯にして、下のセクションへ読み進めてもらえる見た目にしている。
 */
const buttons = [
  { key: "request", label: "資料をダウンロード", to: PATH.REQUEST.to, icon: <FiDownload />, primary: true },
  { key: "meeting", label: "オンライン相談を予約", to: PATH.MEETING_BOOKING.websiteTo, icon: <FiCalendar /> },
  { key: "inquiry", label: "お問い合わせ", to: PATH.INQUIRY_CONTACT.to, icon: <FiMail /> },
];

/** サービス資料の実際のページ構成に合わせている。資料の改訂時はここも見直す */
const documentContents = [
  "リファラル採用の成功に必要な7つの項目",
  "1,000社超の支援実績と導入事例",
  "料金プランとご利用開始までの流れ",
];

export const AskUsCompact: React.FC = () => {
  const { contentfulDownload } = useStaticQuery<GatsbyTypes.AskUsCompactDocumentQuery>(graphql`
    query AskUsCompactDocument {
      contentfulDownload(pathSlug: { eq: "request" }) {
        documentCover {
          gatsbyImageData(width: 480, layout: CONSTRAINED, placeholder: NONE)
        }
      }
    }
  `);
  const cover = contentfulDownload?.documentCover?.gatsbyImageData;

  return (
    <>
      <section className="section" aria-labelledby="askUsCompactTitle">
        <div className="banner">
          <div className="main">
            {cover && (
              <Link className="docLink" to={PATH.REQUEST.to} tabIndex={-1} aria-hidden="true">
                <div className="doc">
                  <span className="page pageBack2" />
                  <span className="page pageBack1" />
                  <div className="page pageFront">
                    <GatsbyImage image={cover} alt="" />
                  </div>
                </div>
              </Link>
            )}
            <div className="text">
              <h2 id="askUsCompactTitle" className="title">
                <span className="phrase">導入のご検討に必要な情報を</span>
                <span className="phrase">サービス資料にまとめています</span>
              </h2>
              <ul className="contents">
                {documentContents.map((item) => (
                  <li key={item} className="contentsItem">
                    <span className="check" aria-hidden="true">
                      <FiCheck />
                    </span>
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>
          <div className="actions">
            {buttons.map((button) => (
              <Link key={button.key} className={button.primary ? "cta ctaPrimary" : "cta"} to={button.to}>
                {button.label}
                {button.icon}
              </Link>
            ))}
          </div>
        </div>
      </section>

      <style jsx>{`
        .section {
          padding: 0 24px;
          background: ${colors.white};
        }
        .banner {
          display: flex;
          align-items: center;
          gap: 32px;
          max-width: 1000px;
          margin: 0 auto;
          padding: 32px 36px;
          box-sizing: border-box;
          border-radius: 12px;
          background: ${colors.lightOrange};
        }
        .main {
          display: flex;
          align-items: center;
          flex: 1;
          gap: 28px;
          min-width: 0;
        }
        .main :global(.docLink) {
          display: block;
          flex: none;
        }
        .doc {
          position: relative;
          width: 240px;
          aspect-ratio: 16 / 9;
          margin: 16px 16px 0 0;
        }
        .page {
          position: absolute;
          inset: 0;
          overflow: hidden;
          border: 1px solid rgba(66, 63, 60, 0.08);
          border-radius: 4px;
          background: ${colors.white};
        }
        .pageBack2 {
          transform: translate(16px, -16px);
        }
        .pageBack1 {
          transform: translate(8px, -8px);
          box-shadow: 0 1px 3px rgba(66, 63, 60, 0.08);
        }
        .pageFront {
          box-shadow: 0 6px 18px rgba(66, 63, 60, 0.16);
        }
        .pageFront :global(.gatsby-image-wrapper) {
          width: 100%;
          height: 100%;
        }
        .text {
          flex: 1;
          min-width: 0;
        }
        .title {
          margin: 0;
          color: ${colors.defaultText};
          font-size: 20px;
          font-weight: bold;
          line-height: 1.5;
          letter-spacing: 0.02em;
        }
        .phrase {
          display: inline-block;
        }
        .contents {
          margin: 10px 0 0;
          padding: 0;
          list-style: none;
        }
        .contentsItem {
          display: flex;
          align-items: flex-start;
          gap: 6px;
          color: ${colors.defaultText};
          font-size: 15px;
          line-height: 1.7;
        }
        .contentsItem + .contentsItem {
          margin-top: 4px;
        }
        .check {
          display: inline-flex;
          flex: none;
          align-items: center;
          height: 1.7em;
          color: #e55c00;
        }
        .check :global(svg) {
          width: 16px;
          height: 16px;
          stroke-width: 3;
        }
        .actions {
          display: flex;
          flex-direction: column;
          flex: none;
          gap: 10px;
          width: 240px;
        }
        .actions :global(.cta) {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 6px;
          height: 48px;
          padding: 0 20px;
          box-sizing: border-box;
          border: 1px solid #e55c00;
          border-radius: 999px;
          background: ${colors.white};
          color: #e55c00;
          font-size: 15px;
          font-weight: bold;
          white-space: nowrap;
          text-decoration: none;
          transition: opacity 0.2s ease, transform 0.2s ease;
        }
        .actions :global(.ctaPrimary) {
          border-color: transparent;
          background-image: ${colors.brandGradient};
          background-color: ${colors.brand};
          color: ${colors.white};
        }
        .actions :global(.cta:hover) {
          opacity: 0.85;
          transform: translate3d(0, -1px, 0);
        }
        .actions :global(.cta svg) {
          flex: none;
          width: 16px;
          height: 16px;
        }

        @media (${breakPoints.tablet}) {
          .banner {
            gap: 24px;
            padding: 28px 32px;
          }
          .main {
            flex-direction: column;
            align-items: flex-start;
            gap: 16px;
          }
          .doc {
            width: 200px;
            margin: 12px 12px 0 0;
          }
          .pageBack2 {
            transform: translate(12px, -12px);
          }
          .pageBack1 {
            transform: translate(6px, -6px);
          }
          .contentsItem {
            font-size: 14px;
          }
        }

        @media (${breakPoints.sp}) {
          .section {
            padding: 0 16px;
          }
          .banner {
            flex-direction: column;
            align-items: stretch;
            gap: 24px;
            padding: 28px 20px 24px;
          }
          .main {
            align-items: stretch;
          }
          .main :global(.docLink) {
            align-self: center;
          }
          .doc {
            width: 220px;
          }
          .title {
            font-size: 18px;
          }
          .actions {
            width: 100%;
          }
        }
      `}</style>
    </>
  );
};
